// src/pages/scheduler/controls.tsx — one renderer per config-control type.
// Verbatim port of the prototype scheduler-controls.jsx into TS. <ConfigControl>
// dispatches on item.type; an unknown type renders a labelled raw-JSON fallback
// so a typo is visible, not silent.

import { useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import { C, F } from "../../theme";
import * as Icons from "../../components/ui/Icon";
import { Toggle, Segmented } from "./kit";
import { DOW, type ConfigControl as Ctl } from "./types";

const UNIT_OPTS: Array<"minutes" | "hours" | "days"> = ["minutes", "hours", "days"];

function ControlShell({ label, help, children, badge }: { label: string; help?: string; children: ReactNode; badge?: ReactNode }) {
  return (
    <div style={{ padding: "14px 0", borderTop: `1px solid ${C.hairlineLt}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: F.body, fontSize: 14.5, fontWeight: 800, color: C.ink, letterSpacing: 0.1, lineHeight: 1.3 }}>{label}</div>
          {help && <div style={{ fontSize: 12.5, color: C.inkMute, fontWeight: 600, lineHeight: 1.45, marginTop: 3, maxWidth: 520 }}>{help}</div>}
        </div>
        {badge}
      </div>
      <div style={{ marginTop: 11 }}>{children}</div>
    </div>
  );
}

function Stepper({ value, min = 0, max = 999, step = 1, onChange, width = 72 }: { value: number; min?: number; max?: number; step?: number; onChange: (v: number) => void; width?: number }) {
  const set = (v: number) => onChange(Math.max(min, Math.min(max, v)));
  const btn: CSSProperties = { width: 38, height: 44, border: `1px solid ${C.hairline}`, background: C.card, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.brassDk };
  return (
    <div style={{ display: "inline-flex", borderRadius: 12, overflow: "hidden", border: `1px solid ${C.hairline}`, boxShadow: C.shadowS }}>
      <button type="button" onClick={() => set(value - step)} style={{ ...btn, borderTop: "none", borderBottom: "none", borderLeft: "none", borderRadius: 0 }}><Icons.Down s={16} c={C.brassDk} /></button>
      <input value={value} onChange={(e) => { const n = parseFloat(e.target.value); if (!isNaN(n)) set(n); }} style={{ width, textAlign: "center", border: "none", outline: "none", background: C.paperSoft, fontFamily: F.mono, fontSize: 17, fontWeight: 700, color: C.ink }} />
      <button type="button" onClick={() => set(value + step)} style={{ ...btn, borderTop: "none", borderBottom: "none", borderRight: "none", borderRadius: 0 }}><Icons.Up s={16} c={C.brassDk} /></button>
    </div>
  );
}

const inputStyle = (): CSSProperties => ({
  width: "100%", boxSizing: "border-box", padding: "11px 14px", borderRadius: 12,
  border: `1px solid ${C.hairline}`, background: C.card, fontFamily: F.body, fontSize: 14.5, fontWeight: 600,
  color: C.ink, outline: "none", resize: "vertical", lineHeight: 1.5,
});

type CtlProps = { item: Ctl; onChange: (v: unknown) => void };

function ToggleControl({ item, onChange }: CtlProps) {
  const on = !!item.value;
  return (
    <ControlShell label={item.label} help={item.help}>
      <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
        <Toggle on={on} onToggle={() => onChange(!on)} />
        <span style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700, color: on ? C.sageDk : C.inkMute }}>{on ? "On" : "Off"}</span>
      </label>
    </ControlShell>
  );
}

function IntervalControl({ item, onChange }: CtlProps) {
  const v = (item.value as { every: number; unit: "minutes" | "hours" | "days" }) || { every: 1, unit: "minutes" };
  return (
    <ControlShell label={item.label} help={item.help}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 14, color: C.inkSoft, fontWeight: 600 }}>Every</span>
        <Stepper value={v.every} min={1} max={999} onChange={(n) => onChange({ ...v, every: n })} />
        <Segmented value={v.unit} options={UNIT_OPTS.map((u) => ({ value: u, label: u }))} onChange={(u) => onChange({ ...v, unit: u })} />
      </div>
    </ControlShell>
  );
}

function TimeControl({ item, onChange }: CtlProps) {
  return (
    <ControlShell label={item.label} help={item.help}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#2B2417", borderRadius: 12, padding: "8px 14px", border: `1px solid ${C.brassDk}` }}>
        <Icons.Clock s={18} c={C.brassGlow} />
        <input type="time" value={String(item.value ?? "")} onChange={(e) => onChange(e.target.value)} style={{ border: "none", outline: "none", background: "transparent", fontFamily: F.mono, fontSize: 22, fontWeight: 700, color: C.brassGlow, letterSpacing: 1, colorScheme: "dark" }} />
      </div>
    </ControlShell>
  );
}

const DAY_LABEL: Record<string, string> = { mon: "M", tue: "T", wed: "W", thu: "T", fri: "F", sat: "S", sun: "S" };

function DaysControl({ item, onChange }: CtlProps) {
  const sel = (item.value as string[]) || [];
  const toggle = (d: string) => onChange(sel.includes(d) ? sel.filter((x) => x !== d) : [...DOW].filter((x) => sel.includes(x) || x === d));
  return (
    <ControlShell label={item.label} help={item.help}>
      <div style={{ display: "flex", gap: 6 }}>
        {DOW.map((d) => {
          const on = sel.includes(d);
          return (
            <button key={d} type="button" onClick={() => toggle(d)} title={d} style={{
              width: 42, height: 42, borderRadius: 12, cursor: "pointer",
              border: `1px solid ${on ? C.brassDk : C.hairline}`, background: on ? C.brassDk : C.card,
              color: on ? C.brassGlow : C.inkMute, fontFamily: F.body, fontWeight: 800, fontSize: 14,
              boxShadow: on ? "inset 0 1px 0 rgba(244,226,168,0.25)" : C.shadowS,
            }}>{DAY_LABEL[d]}</button>
          );
        })}
      </div>
    </ControlShell>
  );
}

function DateControl({ item, onChange }: CtlProps) {
  return (
    <ControlShell label={item.label} help={item.help}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: C.card, borderRadius: 12, padding: "10px 14px", border: `1px solid ${C.hairline}`, boxShadow: C.shadowS }}>
        <Icons.Cal s={18} c={C.brassDk} />
        <input type="date" value={String(item.value ?? "")} onChange={(e) => onChange(e.target.value)} style={{ border: "none", outline: "none", background: "transparent", fontFamily: F.mono, fontSize: 15, fontWeight: 700, color: C.ink }} />
      </div>
    </ControlShell>
  );
}

function NumberControl({ item, onChange }: CtlProps) {
  return (
    <ControlShell label={item.label} help={item.help}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Stepper value={Number(item.value ?? 0)} min={item.min} max={item.max} step={item.step || 1} onChange={onChange} />
        {item.unit && <span style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 15, color: C.inkSoft, fontWeight: 600 }}>{item.unit}</span>}
      </div>
    </ControlShell>
  );
}

function SliderControl({ item, onChange }: CtlProps) {
  const val = Number(item.value ?? 0);
  const min = item.min ?? 0;
  const max = item.max ?? 100;
  const pct = ((val - min) / (max - min)) * 100;
  return (
    <ControlShell label={item.label} help={item.help}
      badge={<span style={{ marginLeft: "auto", fontFamily: F.mono, fontSize: 15, fontWeight: 700, color: C.brassDk }}>{val}{item.unit ? ` ${item.unit}` : ""}</span>}>
      <div style={{ position: "relative", height: 36, display: "flex", alignItems: "center" }}>
        <div style={{ position: "absolute", left: 0, right: 0, height: 8, borderRadius: 6, background: C.paperWarm, border: `1px solid ${C.hairline}` }} />
        <div style={{ position: "absolute", left: 0, width: `${pct}%`, height: 8, borderRadius: 6, background: `linear-gradient(90deg,${C.brassLt},${C.brassDk})` }} />
        <input type="range" min={min} max={max} step={item.step || 1} value={val} onChange={(e) => onChange(parseFloat(e.target.value))}
          style={{ position: "absolute", left: 0, right: 0, width: "100%", margin: 0, appearance: "none", background: "transparent", cursor: "pointer", height: 36 }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontFamily: F.mono, fontSize: 11, color: C.inkMute, fontWeight: 700, marginTop: 2 }}>
        <span>{min}{item.unit ? ` ${item.unit}` : ""}</span><span>{max}{item.unit ? ` ${item.unit}` : ""}</span>
      </div>
    </ControlShell>
  );
}

function TextControl({ item, onChange }: CtlProps) {
  return (
    <ControlShell label={item.label} help={item.help}>
      {item.multiline
        ? <textarea value={String(item.value ?? "")} onChange={(e) => onChange(e.target.value)} rows={3} style={inputStyle()} />
        : <input value={String(item.value ?? "")} onChange={(e) => onChange(e.target.value)} style={inputStyle()} />}
    </ControlShell>
  );
}

function TemplateControl({ item, onChange }: CtlProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const insert = (tok: string) => {
    const el = ref.current; const v = String(item.value ?? "");
    const pos = el ? el.selectionStart : v.length;
    onChange(v.slice(0, pos) + tok + v.slice(el ? el.selectionEnd : v.length));
  };
  return (
    <ControlShell label={item.label} help={item.help}>
      <textarea ref={ref} value={String(item.value ?? "")} onChange={(e) => onChange(e.target.value)} rows={3} style={inputStyle()} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, alignItems: "center" }}>
        <span style={{ fontSize: 11, color: C.inkMute, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase" }}>Insert</span>
        {(item.tokens || []).map((t) => (
          <button key={t} type="button" onClick={() => insert(t)} style={{ fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: C.heatherDk, background: C.heatherBg, border: "1px solid rgba(122,93,140,0.25)", borderRadius: 7, padding: "4px 8px", cursor: "pointer" }}>{t}</button>
        ))}
      </div>
    </ControlShell>
  );
}

function SelectControl({ item, onChange }: CtlProps) {
  const opts = item.options || [];
  if (opts.length <= 3) {
    return (
      <ControlShell label={item.label} help={item.help}>
        <Segmented value={String(item.value ?? "")} options={opts} onChange={onChange} />
      </ControlShell>
    );
  }
  return (
    <ControlShell label={item.label} help={item.help}>
      <div style={{ position: "relative", display: "inline-block" }}>
        <select value={String(item.value ?? "")} onChange={(e) => onChange(e.target.value)} style={{ appearance: "none", padding: "11px 40px 11px 14px", borderRadius: 12, border: `1px solid ${C.hairline}`, background: C.card, boxShadow: C.shadowS, fontFamily: F.body, fontSize: 14.5, fontWeight: 700, color: C.ink, cursor: "pointer", outline: "none", minWidth: 200 }}>
          {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><Icons.Down s={16} c={C.brassDk} /></span>
      </div>
    </ControlShell>
  );
}

function MultiSelectControl({ item, onChange }: CtlProps) {
  const sel = (item.value as string[]) || [];
  const toggle = (v: string) => onChange(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
  return (
    <ControlShell label={item.label} help={item.help}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {(item.options || []).map((o) => {
          const on = sel.includes(o.value);
          return (
            <button key={o.value} type="button" onClick={() => toggle(o.value)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 13px", borderRadius: 999, cursor: "pointer", border: `1px solid ${on ? C.sageDk : C.hairline}`, background: on ? C.sageBg : C.card, color: on ? C.sageDk : C.inkSoft, fontFamily: F.body, fontWeight: 700, fontSize: 13.5 }}>
              <span style={{ width: 16, height: 16, borderRadius: 5, flexShrink: 0, background: on ? C.sageDk : "transparent", border: `1.5px solid ${on ? C.sageDk : C.hairline}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {on && <Icons.Check s={11} c="#fff" />}
              </span>
              {o.label}
            </button>
          );
        })}
      </div>
    </ControlShell>
  );
}

function RecipientsControl({ item, onChange }: CtlProps) {
  const list = (item.value as { name: string; phone: string }[]) || [];
  const update = (i: number, patch: Partial<{ name: string; phone: string }>) => onChange(list.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { name: "New recipient", phone: "" }]);
  return (
    <ControlShell label={item.label} help={item.help}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {list.map((r, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: C.paperSoft, border: `1px solid ${C.hairlineLt}`, borderRadius: 12, padding: "8px 8px 8px 12px" }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: C.brassBg, border: `1px solid ${C.brassLt}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icons.Phone s={15} c={C.brassDk} />
            </div>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
              <input value={r.name} onChange={(e) => update(i, { name: e.target.value })} style={{ border: "none", outline: "none", background: "transparent", fontFamily: F.body, fontSize: 14, fontWeight: 800, color: C.ink }} />
              <input value={r.phone} onChange={(e) => update(i, { phone: e.target.value })} placeholder="Phone or rule" style={{ border: "none", outline: "none", background: "transparent", fontFamily: F.mono, fontSize: 12.5, fontWeight: 600, color: C.inkSoft }} />
            </div>
            <button type="button" onClick={() => remove(i)} aria-label="Remove" style={{ width: 32, height: 32, borderRadius: 9, border: `1px solid ${C.hairlineLt}`, background: C.card, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Icons.Close s={14} c={C.inkMute} /></button>
          </div>
        ))}
        <button type="button" onClick={add} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, alignSelf: "flex-start", background: "transparent", border: `1px dashed ${C.hairline}`, color: C.brassDk, fontFamily: F.body, fontWeight: 700, fontSize: 13, padding: "9px 14px", borderRadius: 11, cursor: "pointer" }}><Icons.Plus s={15} c={C.brassDk} /> Add recipient</button>
      </div>
    </ControlShell>
  );
}

const CONTROL_MAP: Record<string, (p: CtlProps) => JSX.Element> = {
  toggle: ToggleControl, interval: IntervalControl, time: TimeControl, days: DaysControl,
  date: DateControl, number: NumberControl, slider: SliderControl, text: TextControl,
  template: TemplateControl, select: SelectControl, multiselect: MultiSelectControl, recipients: RecipientsControl,
};

export function ConfigControl({ item, onChange }: CtlProps) {
  const Comp = CONTROL_MAP[item.type];
  if (!Comp) {
    return (
      <ControlShell label={item.label || item.key} help={`Unknown control type "${item.type}"`}>
        <code style={{ fontFamily: F.mono, fontSize: 12, color: C.signalDk }}>{JSON.stringify(item.value)}</code>
      </ControlShell>
    );
  }
  return <Comp item={item} onChange={onChange} />;
}
